import type { FavoriteViewsModalState } from './FavoriteViewsManagementModal'
import type { FavoriteView, FavoriteViewFolder } from '@/api/favorites'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getFavoriteViewShareLink } from '@/api/favorites'
import { useToasterContext } from '@/contexts/toaster/useToasterContext'
import {
  useCreateFavoriteViewFolder,
  useDeleteFavoriteView,
  useDeleteFavoriteViewFolder,
  useUpdateFavoriteView,
  useUpdateFavoriteViewFolder,
} from '@/hooks/favorites/useFavoriteViews'

interface UseFavoriteViewsPageActionsOptions {
  onError: () => void
  onFolderDeleted: () => void
}

export function useFavoriteViewsPageActions({ onError, onFolderDeleted }: Readonly<UseFavoriteViewsPageActionsOptions>) {
  const { t } = useTranslation()
  const { showToast } = useToasterContext()
  const createFolder = useCreateFavoriteViewFolder()
  const deleteFolderMutation = useDeleteFavoriteViewFolder()
  const updateFolder = useUpdateFavoriteViewFolder()
  const updateView = useUpdateFavoriteView()
  const deleteView = useDeleteFavoriteView()
  const [isFolderDrawerOpen, setIsFolderDrawerOpen] = useState(false)
  const [editingFolder, setEditingFolder] = useState<FavoriteViewFolder | null>(null)
  const [activeModal, setActiveModal] = useState<FavoriteViewsModalState | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const openCreateFolder = () => {
    setEditingFolder(null)
    setIsFolderDrawerOpen(true)
  }

  const saveFolder = async ({ name, emoji }: { name: string, emoji: string }) => {
    try {
      if (editingFolder)
        await updateFolder.mutateAsync({ folderId: editingFolder.id, input: { name, emoji } })
      else
        await createFolder.mutateAsync({ name, emoji })
      setIsFolderDrawerOpen(false)
      setEditingFolder(null)
    }
    catch {
      onError()
    }
  }

  const closeFolderDrawer = () => {
    setIsFolderDrawerOpen(false)
    setEditingFolder(null)
  }

  const openFolderEditor = (folder: FavoriteViewFolder) => {
    setEditingFolder(folder)
    setIsFolderDrawerOpen(true)
  }

  const openModal = (modal: FavoriteViewsModalState) => {
    setActiveModal(modal)
    setIsModalOpen(true)
  }

  const closeModal = () => setIsModalOpen(false)

  const handleModalClosed = () => {
    setIsModalOpen(false)
    setActiveModal(null)
  }

  const deleteSelectedFolder = async () => {
    if (activeModal?.type !== 'delete-folder')
      return

    try {
      await deleteFolderMutation.mutateAsync(activeModal.folder.id)
      closeModal()
      showToast({ variant: 'success', message: t('favorite-views.management.delete-folder-success') })
      onFolderDeleted()
    }
    catch {
      onError()
    }
  }

  const renameView = async (view: FavoriteView, title: string) => {
    try {
      await updateView.mutateAsync({ viewId: view.id, input: { title } })
      return true
    }
    catch {
      onError()
      return false
    }
  }

  const requestShareLink = async (view: FavoriteView) => {
    try {
      const result = await getFavoriteViewShareLink(view.id)
      openModal({ type: 'share', view, url: result.url })
    }
    catch {
      onError()
    }
  }

  const deleteSelectedView = async () => {
    if (activeModal?.type !== 'delete-view')
      return

    try {
      await deleteView.mutateAsync(activeModal.view.id)
      closeModal()
      showToast({ variant: 'success', message: t('favorite-views.management.delete-view-success') })
    }
    catch {
      onError()
    }
  }

  const copyShareLink = async () => {
    if (activeModal?.type !== 'share')
      return

    try {
      await navigator.clipboard.writeText(activeModal.url)
      closeModal()
      showToast({ variant: 'success', message: t('favorite-views.management.share-link-copied') })
    }
    catch {
      onError()
    }
  }

  const validateActiveModal = () => {
    if (activeModal?.type === 'delete-folder')
      void deleteSelectedFolder()
    else if (activeModal?.type === 'delete-view')
      void deleteSelectedView()
    else if (activeModal?.type === 'share')
      void copyShareLink()
  }

  return {
    activeModal,
    closeFolderDrawer,
    closeModal,
    editingFolder,
    handleModalClosed,
    isDeleteFolderPending: deleteFolderMutation.isPending,
    isDeleteViewPending: deleteView.isPending,
    isFolderDrawerOpen,
    isModalOpen,
    openCreateFolder,
    openDeleteFolder: (folder: FavoriteViewFolder) => openModal({ type: 'delete-folder', folder }),
    openDeleteView: (view: FavoriteView) => openModal({ type: 'delete-view', view }),
    openFolderEditor,
    renameView,
    requestShareLink,
    saveFolder,
    validateActiveModal,
  }
}
